import React, { useState, useEffect } from 'react';
import './App.css';
import './util/utility.css';
import { PersistGate } from 'redux-persist/integration/react';
import { Provider } from 'react-redux';
import Cookies from 'universal-cookie';
import { Amplify, Auth } from 'aws-amplify';
import configureStore from './redux/store';
import { SET_DARK_MODE } from './redux/action-constants';
import awsConfig from './aws-exports';
import AppContent from './containers/AppContent';
import { ThemeProvider, createTheme, alpha, darken, lighten } from '@mui/material';
import { StyledEngineProvider } from '@mui/material/styles';
import { ThemeProvider as LegacyThemeProvider } from '@mui/styles';

const { store, persistor } = configureStore();

Amplify.configure(awsConfig);

// Mocks parts of the Auth object when running Cypress
if (window.Cypress) {
  const mockJwt = 'mocked-jwt-token-from-cypress';
  const mockSession = {
    isValid: () => true,
    getIdToken: () => ({
      getJwtToken: () => mockJwt,
    }),
    getAccessToken: () => ({
      getJwtToken: () => mockJwt,
    }),
    getRefreshToken: () => ({
      getToken: () => 'mock-refresh-token',
    }),
    idToken: { jwtToken: mockJwt },
    accessToken: { jwtToken: mockJwt },
  };
  const mockCognitoUser = {
    username: 'damon.greenhalgh@datacom.com',
    attributes: { email: 'damon.greenhalgh@datacom.com' },
    getSession: (callback) => {
      if (callback) callback(null, mockSession);
      return Promise.resolve(mockSession);
    },
    getUserAttributes: (callback) => {
      if (callback) callback(null, []);
      return Promise.resolve([]);
    },
  };

  Auth.currentAuthenticatedUser = () => Promise.resolve(mockCognitoUser);
  Auth.currentSession = () => Promise.resolve(mockSession);
  Auth.federatedSignIn = () => Promise.resolve();
}

/**
 * Refer to the following MUI theme structure,
 * https://mui.com/material-ui/customization/default-theme
 * Use this structure whenever creating a new theme.
 */

/**
 * Theme colours can be derived from a 'base' colour + alpha manipulations.
 * Using this method enables us to do custom themes, i.e. user picks their
 * own theme. Colours with 'Base' in the name are core colours and are the ones
 * the user/dev selects. The theme will reference these values so it makes it
 * easier to adjust on the
 */

const darkThemeConfig = {
  type: 'dark',
  primary: 'rgb(59, 178, 204)', // sky blue +1
  secondary: 'rgb(59, 178, 204)',
  tertiary: 'rgb(106, 130, 151)',
  accent1: 'rgb(255, 86, 159)',
  accent2: 'rgb(235, 103, 27)',
  accent3: 'rgb(110, 229, 255)',
  accent4: 'rgb(204, 246, 243)',
  accent5: 'rgb(99, 123, 243)',
  lowContrast: 'rgb(204, 208, 223)',
  midContrast: 'rgb(213, 232, 250)',
  highContrast: 'rgb(255, 255, 255)',
  primaryFill: 'rgb(0, 10, 20)', // midnight blue
  secondaryFill: 'rgb(10, 21, 32)',
  success: 'rgb(52, 157, 60)',
  warning: 'rgb(211, 157, 51)',
  error: 'rgba(255, 0, 0, 1)',
  custom: {
    tableAltRowBg: 'rgb(18, 19, 20)',
    pdcCheckContainerBg: 'rgb(5, 5, 5)',
    workOrderInstructions: 'rgb(213, 232, 250)',
  },
  derived: {
    accentMain: 0.7,
    accentDark: 0.9,
    accentLight: 0.5,
    divider: 0,
    disabled: 0.25,
    hover: 0.06,
    chipFill: 0.1,
    chipContrast: 0.2,
  },
};

const lightThemeConfig = {
  type: 'light',
  primary: 'rgb(0, 43, 254)', // electric blue
  secondary: 'rgb(0, 43, 254)',
  tertiary: 'rgb(106, 130, 151)',
  accent1: 'rgb(255, 0, 112)',
  accent2: 'rgb(255, 92, 0)',
  accent3: 'rgb(0, 185, 227)',
  accent4: 'rgb(0, 183, 171)',
  accent5: 'rgb(71, 93, 206)',
  lowContrast: 'rgb(128, 141, 154)',
  midContrast: 'rgb(106, 128, 147)',
  highContrast: '#000A14',
  primaryFill: 'rgb(250, 250, 250)',
  secondaryFill: 'rgb(255, 255, 255)',
  success: '#40b549',
  warning: '#f0b02c',
  error: '#fa0000',
  custom: {
    // custom variables that are used rarely or for very specific purposes
    tableAltRowBg: 'rgb(255, 255, 255)', // used for table alternate rows and power check main calc container
    pdcCheckContainerBg: 'rgb(235, 235, 235)', // use for pdc check container
    workOrderInstructions: '#01157f', // use for work order instructions font and border
  },
  derived: {
    // opacity constants
    accentMain: 0.7,
    accentDark: 0.9,
    accentLight: 0.5,
    divider: 0.2,
    disabled: 0.25,
    hover: 0.03,
    chipFill: 0.1,
    chipContrast: 0.1,
  },
};

const applyTheme = (config) => {
  const darkMode = config.type === 'dark';

  return createTheme({
    shape: {
      borderRadius: 4,
    },
    // MUI component overrides
    components: {
      MuiAvatar: {
        styleOverrides: {
          root: {
            backgroundColor: config.primary,
          },
        },
      },
      MuiStepConnector: {
        styleOverrides: {
          line: {
            borderColor: alpha(config.lowContrast, config.derived.divider),
          },
        },
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: '8px',
          },
        },
        variants: [
          {
            props: { variant: 'outlined', color: 'tertiary' },
            style: {
              backgroundColor: config.secondaryFill,
            },
          },
        ],
      },
      MuiToggleButtonGroup: {
        styleOverrides: {
          root: {
            textWrap: 'nowrap',
          },
          middleButton: {
            borderLeft: `1px solid ${alpha(config.lowContrast, config.derived.divider)}`,
          },
          lastButton: {
            borderLeft: `1px solid ${alpha(config.lowContrast, config.derived.divider)}`,
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            '&.Mui-selected': {
              backgroundColor: config.primary,
              color: 'rgba(255, 255, 255, 1)',
            },
            '&:hover': {
              backgroundColor: alpha(config.primary, config.derived.accentLight),
              color: 'rgba(255, 255, 255, 1)',
            },
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          input: {
            padding: '16.5px 0px 16.5px 14px',
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            justifyContent: 'start',
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: `1px solid ${alpha(config.lowContrast, config.derived.divider)}`, // bottom borders of table rows
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            paddingRight: 0,
          },
          notchedOutline: {
            borderColor: alpha(config.lowContrast, config.derived.disabled), // form input 'outlined' border colour
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            padding: 16,
            borderRadius: 12,
            backgroundColor: config.secondaryFill,
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            paddingBottom: 0,
          },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            gap: 8,
            paddingTop: 16,
            paddingRight: 24,
            paddingBottom: 16,
            paddingLeft: 24,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: config.primaryFill,
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            backgroundColor: config.primaryFill,
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          paper: {
            backgroundColor: config.primaryFill,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 16,
          },
        },
      },
      MuiTableSortLabel: {
        styleOverrides: {
          root: {
            flexDirection: 'row',
          },
        },
      },
      MuiTablePagination: {
        styleOverrides: {
          root: {
            borderTop: `1px solid ${alpha(config.lowContrast, config.derived.divider)}`,
          },
        },
      },
    },
    typography: {
      fontFamily: ['Montserrat', 'sans-serif'].join(','),
      button: {
        fontFamily: ['Montserrat-Button', 'sans-serif'].join(','),
      },
      h6: {
        fontFamily: ['Montserrat-Header', 'sans-serif'].join(','),
        color: config.highContrast,
      },
      h5: {
        fontFamily: ['Montserrat-Header', 'sans-serif'].join(','),
        color: config.lowContrast,
      },
      h4: {
        fontFamily: ['Montserrat-Header', 'sans-serif'].join(','),
        color: config.highContrast,
      },
      overline: {
        color: config.midContrast,
        fontWeight: 'bold',
        textTransform: 'capitalize',
      },
      body1: {
        color: config.lowContrast,
      },
      body2: {
        color: config.lowContrast,
      },
      caption: {
        color: config.lowContrast,
      },
    },
    palette: {
      mode: config.type,
      type: config.type,
      // Usually refering to input components and sub actions
      action: {
        active: alpha(config.primary, config.derived.accentDark), // e.g. arrows, close btn on inputs
        hover: alpha(config.lowContrast, config.derived.hover), // e.g. hover over table rows
        disabled: alpha(config.lowContrast, config.derived.disabled), // e.g. outlines for disabled inputs
        disabledBackground: alpha(config.lowContrast, config.derived.disabled), // e.g backgrounds for disabled inputs, e.g. send and save
        selected: 'rgba(0, 0, 0, 0.08)',
        focus: alpha(config.primary, 0.05),
      },
      contrastThreshold: 3, // default
      divider: alpha(config.lowContrast, config.derived.divider),
      primary: {
        main: config.primary,
        light: alpha(config.primary, config.derived.accentLight),
        dark: alpha(config.primary, config.derived.accentDark),
        contrastText: 'rgba(255, 255, 255, 1)',
      },
      secondary: {
        main: config.secondary,
        light: alpha(config.secondary, config.derived.accentLight),
        dark: alpha(config.secondary, config.derived.accentDark),
        contrastText: 'rgba(255, 255, 255, 1)',
      },
      accent1: {
        main: alpha(config.accent1, config.derived.chipFill),
        light: alpha(config.accent1, config.derived.accentLight),
        dark: alpha(config.accent1, config.derived.accentDark),
        contrastText: darkMode
          ? lighten(config.accent1, config.derived.chipContrast)
          : darken(config.accent1, config.derived.chipContrast),
      },
      accent2: {
        main: alpha(config.accent2, config.derived.chipFill),
        light: alpha(config.accent2, config.derived.accentLight),
        dark: alpha(config.accent2, config.derived.accentDark),
        contrastText: darkMode
          ? lighten(config.accent2, config.derived.chipContrast)
          : darken(config.accent2, config.derived.chipContrast),
      },
      accent3: {
        main: alpha(config.accent3, config.derived.chipFill),
        light: alpha(config.accent3, config.derived.accentLight),
        dark: alpha(config.accent3, config.derived.accentDark),
        contrastText: darkMode
          ? lighten(config.accent3, config.derived.chipContrast)
          : darken(config.accent3, config.derived.chipContrast),
      },
      accent4: {
        main: alpha(config.accent4, config.derived.chipFill),
        light: alpha(config.accent4, config.derived.accentLight),
        dark: alpha(config.accent4, config.derived.accentDark),
        contrastText: darkMode
          ? lighten(config.accent4, config.derived.chipContrast)
          : darken(config.accent4, config.derived.chipContrast),
      },
      accent5: {
        main: alpha(config.accent5, config.derived.chipFill),
        light: alpha(config.accent5, config.derived.accentLight),
        dark: alpha(config.accent5, config.derived.accentDark),
        contrastText: darkMode
          ? lighten(config.accent5, config.derived.chipContrast)
          : darken(config.accent5, config.derived.chipContrast),
      },
      error: {
        main: config.error,
        light: alpha(config.error, config.derived.accentLight),
        dark: alpha(config.error, config.derived.accentDark),
        contrastText: 'rgba(255, 255, 255, 1)',
      },
      warning: {
        main: config.warning,
        light: alpha(config.warning, config.derived.accentLight),
        dark: alpha(config.warning, config.derived.accentDark),
        contrastText: 'rgba(255, 255, 255, 1)',
      },
      success: {
        main: config.success,
        light: alpha(config.success, config.derived.accentLight),
        dark: alpha(config.success, config.derived.accentDark),
        contrastText: 'rgba(255, 255, 255, 1)',
      },
      background: {
        paper: config.primaryFill,
        default: config.secondaryFill,
        tableAltRowBg: config.custom.tableAltRowBg,
        pdcCheckContainerBg: config.custom.pdcCheckContainerBg,
      },
      text: {
        primary: config.highContrast,
        secondary: config.midContrast,
        disabled: config.lowContrast,
      },
      lighterror: {
        main: alpha(config.error, config.derived.chipFill),
        light: alpha(config.error, config.derived.accentLight),
        dark: alpha(config.error, config.derived.accentDark),
        contrastText: darkMode
          ? lighten(config.error, config.derived.chipContrast)
          : darken(config.error, config.derived.chipContrast),
      },
      lightwarning: {
        main: alpha(config.warning, config.derived.chipFill),
        light: alpha(config.warning, config.derived.accentLight),
        dark: alpha(config.warning, config.derived.accentDark),
        contrastText: darkMode
          ? lighten(config.warning, config.derived.chipContrast)
          : darken(config.warning, config.derived.chipContrast),
      },
      lightsuccess: {
        main: alpha(config.success, config.derived.chipFill),
        light: alpha(config.success, config.derived.accentLight),
        dark: alpha(config.success, config.derived.accentDark),
        contrastText: darkMode
          ? lighten(config.success, config.derived.chipContrast)
          : darken(config.success, config.derived.chipContrast),
      },
      grey: {
        50: 'rgb(255, 0, 0)',
        100: 'rgb(255, 0, 0)',
        200: 'rgb(255, 0, 0)',
        300: 'rgb(255, 0, 0)',
        400: 'rgb(255, 0, 0)',
        500: 'rgb(255, 0, 0)',
        600: 'rgb(255, 0, 0)',
        700: alpha(config.primary, config.derived.accentMain), // tooltip bg
        800: 'rgb(255, 0, 0)',
        900: 'rgb(255, 0, 0)',
        A100: 'rgb(255, 0, 255)',
        A200: 'rgb(255, 0, 255)',
        A300: 'rgb(255, 0, 255)',
        A400: 'rgb(255, 0, 255)',
      },
      custom: {
        workOrderInstructions: config.custom.workOrderInstructions,
      },
      tertiary: {
        main: 'rgb(106, 130, 151)',
        light: '#42a5f5',
        dark: '#1565c0',
        contrastText: 'rgb(64, 69, 74)',
      },
      // chip grey/disabled colour
      tertiarylight: {
        main: alpha(config.tertiary, config.derived.chipFill),
        light: alpha(config.tertiary, config.derived.accentLight),
        dark: alpha(config.tertiary, config.derived.accentDark),
        contrastText: darkMode
          ? lighten(config.tertiary, config.derived.chipContrast)
          : darken(config.tertiary, config.derived.chipContrast),
      },
    },
  });
};

function App() {
  const cookies = new Cookies();
  const initialDark = cookies.get('darkMode')
    ? Boolean(JSON.parse(cookies.get('darkMode')))
    : false;
  const [darkMode, setDarkMode] = useState(initialDark);
  const [theme, setTheme] = useState();

  // Sync darkMode with Redux so Settings page can toggle it
  useEffect(() => {
    store.dispatch({ type: SET_DARK_MODE, darkMode: initialDark });

    let prevDarkMode = initialDark;
    const unsubscribe = store.subscribe(() => {
      const reduxDarkMode = store.getState().app.darkMode;
      if (reduxDarkMode !== prevDarkMode) {
        prevDarkMode = reduxDarkMode;
        cookies.set('darkMode', reduxDarkMode, { path: '/' });
        setDarkMode(reduxDarkMode);
      }
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    setTheme(applyTheme(darkMode ? darkThemeConfig : lightThemeConfig));
  }, [darkMode]);

  return (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        {theme && (
          <StyledEngineProvider injectFirst>
            <ThemeProvider theme={theme}>
              <LegacyThemeProvider theme={theme}>
                <AppContent darkMode={darkMode} />
              </LegacyThemeProvider>
            </ThemeProvider>
          </StyledEngineProvider>
        )}
      </PersistGate>
    </Provider>
  );
}

export default App;
